import React from "react";
import { Tag } from "primereact/tag";
import type { SpreadStatistics } from "../../../types/stationPerformance";
import MeasureValueCell from "./MeasureValueCell";
import { spreadBandFor } from "../StationPerformance.consts";

interface SpreadStatisticsPanelProps {
  title: string;
  statistics: SpreadStatistics | null | undefined;
  format?: "number" | "money" | "percent";
  suffix?: string;
  /**
   * How many observations the statistics were computed over. Passed in rather than read off
   * [statistics], which does not carry it: the panel used to reach for a field that was never on the
   * type and rendered a blank where the number should have been.
   */
  observationCount?: number;
}

/**
 * Min / median / max plus scatter for one measure.
 *
 * The coefficient of variation is shown alongside the standard deviation on purpose: raw sigma scales
 * with size, so comparing a busy station to a quiet one on sigma alone just re-ranks them by volume.
 * The CV is what makes "which station is *erratic*" answerable.
 *
 * `estimated` is surfaced rather than hidden: the median comes from histogram buckets, so it is close
 * but not exact, and presenting an estimate as exact would be the wrong kind of confident.
 */
const SpreadStatisticsPanel: React.FC<SpreadStatisticsPanelProps> = ({
  title,
  statistics,
  format = "number",
  suffix = "",
  observationCount,
}) => {
  if (!statistics) return null;

  // Both scatter figures are coloured by the CV, including sigma. Sigma has no scale of its own, so
  // colouring it by its own magnitude would say more about how busy the station is than how erratic.
  const cv = statistics.coefficientOfVariation?.value ?? null;
  const band = cv === null ? null : spreadBandFor(cv);
  const tint = band
    ? { background: band.background, color: band.text }
    : undefined;

  const rows: { label: string; value: React.ReactNode; hint?: string }[] = [
    { label: "Smallest", value: <MeasureValueCell measure={statistics.min} format={format} suffix={suffix} /> },
    { label: "p25", value: <MeasureValueCell measure={statistics.p25} format={format} suffix={suffix} /> },
    { label: "Median", value: <MeasureValueCell measure={statistics.median} format={format} suffix={suffix} /> },
    { label: "p75", value: <MeasureValueCell measure={statistics.p75} format={format} suffix={suffix} /> },
    { label: "Largest", value: <MeasureValueCell measure={statistics.max} format={format} suffix={suffix} /> },
    { label: "Mean", value: <MeasureValueCell measure={statistics.mean} format={format} suffix={suffix} /> },
    {
      label: "Std deviation",
      value: (
        <span
          className="inline-flex border-round px-2 py-1"
          style={tint}
          title={band ? `Scatter is ${band.label}` : undefined}
        >
          <MeasureValueCell measure={statistics.standardDeviation} format={format} suffix={suffix} />
        </span>
      ),
      hint: "Absolute scatter. Grows with the size of the station, so it is coloured by the relative figure below rather than by its own size",
    },
    {
      label: "Variation",
      value: (
        <span
          className="inline-flex border-round px-2 py-1"
          style={tint}
          title={band ? `Scatter is ${band.label}` : undefined}
        >
          <MeasureValueCell measure={statistics.coefficientOfVariation} decimals={3} />
        </span>
      ),
      hint: "Scatter relative to the mean, so a busy and a quiet station can be compared on consistency. 1.0 means the scatter has reached the size of the average",
    },
    {
      label: "Largest − smallest",
      value: <MeasureValueCell measure={statistics.spreadStrongToWeak} format={format} suffix={suffix} />,
    },
  ];

  return (
    <div>
      <div className="flex align-items-center gap-2 mb-2">
        <span className="font-semibold text-sm">{title}</span>
        {observationCount !== undefined && (
          <span className="text-color-secondary text-xs">
            {observationCount} observation{observationCount === 1 ? "" : "s"}
          </span>
        )}
        {band && (
          <Tag
            value={band.label}
            style={{ background: band.background, color: band.text }}
            title="Based on the coefficient of variation: scatter divided by the average"
          />
        )}
        {statistics.estimated && (
          <Tag
            value="median estimated"
            severity="info"
            title="Median and percentiles are interpolated from stored histogram buckets, so they are close but not exact"
          />
        )}
      </div>
      <div className="grid">
        {rows.map((row) => (
          <div key={row.label} className="col-6 md:col-4 mb-2">
            <div className="text-color-secondary text-xs" title={row.hint}>
              {row.label}
              {row.hint && <i className="pi pi-info-circle ml-1" style={{ fontSize: "0.6rem" }} />}
            </div>
            <div className="font-medium">{row.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SpreadStatisticsPanel;
